import { isValidPaymentSelection } from '../../../types/schedule.types';
import type { PaymentSelection, SchedulePreviewParams } from '../../../types/schedule.types';
import type { AppliedDeposit } from '../../../services/appliedDeposit';

/** `editable` carries only the default — min/max live in `deriveDepositBounds` instead,
 *  never duplicated here. */
export type DepositMode =
    | { kind: 'none' }
    | { kind: 'fixed'; amountMinor: number }
    | { kind: 'editable'; defaultMinor: number | undefined };

const isPositiveInteger = (value: number | undefined): value is number =>
    value !== undefined && Number.isInteger(value) && value > 0;

/** A default outside whichever bounds are configured is discarded, never clamped — the
 *  single source both the resolver (rule 2a) and the payment-step prefill gate on. */
export function discardOutOfBoundsDefault(
    defaultMinor: number | undefined,
    minMinor: number | undefined,
    maxMinor: number | undefined,
): number | undefined {
    if (defaultMinor === undefined) {
        return undefined;
    }
    const outOfBounds =
        (minMinor !== undefined && defaultMinor < minMinor) ||
        (maxMinor !== undefined && defaultMinor > maxMinor);
    return outOfBounds ? undefined : defaultMinor;
}

/** Discards an out-of-bounds default AND a non-positive-integer one — `0` is
 *  admin-reachable (`validation_rules.min` is `0`) but never a usable deposit. */
export function usableDefaultDepositMinor(
    defaultMinor: number | undefined,
    minMinor: number | undefined,
    maxMinor: number | undefined,
): number | undefined {
    const inBounds = discardOutOfBoundsDefault(defaultMinor, minMinor, maxMinor);
    return isPositiveInteger(inBounds) ? inBounds : undefined;
}

/** Every fixing rule is gated on `allowDeposit`, never on the amount values alone — see
 *  the resolver's own rule order below for what each branch does. */
export function resolveDepositMode(input: {
    isDepositBearing: boolean;
    allowDeposit: boolean;
    minMinor: number | undefined;
    maxMinor: number | undefined;
    defaultMinor: number | undefined;
}): DepositMode {
    const { isDepositBearing, allowDeposit, minMinor, maxMinor } = input;

    if (!isDepositBearing) {
        return { kind: 'none' };
    }
    if (!allowDeposit) {
        return { kind: 'editable', defaultMinor: undefined };
    }

    const defaultMinor = usableDefaultDepositMinor(input.defaultMinor, minMinor, maxMinor);

    if (defaultMinor !== undefined && minMinor === undefined && maxMinor === undefined) {
        if (isPositiveInteger(defaultMinor)) {
            return { kind: 'fixed', amountMinor: defaultMinor };
        }
    } else if (
        defaultMinor !== undefined &&
        minMinor !== undefined &&
        maxMinor !== undefined &&
        defaultMinor === minMinor &&
        defaultMinor === maxMinor
    ) {
        if (isPositiveInteger(defaultMinor)) {
            return { kind: 'fixed', amountMinor: defaultMinor };
        }
    } else if (minMinor !== undefined && maxMinor !== undefined && minMinor === maxMinor) {
        if (isPositiveInteger(minMinor)) {
            return { kind: 'fixed', amountMinor: minMinor };
        }
    }

    return { kind: 'editable', defaultMinor };
}

export type DepositOrigin = 'confirmed' | 'product' | 'entered' | 'cleared' | 'blank' | 'schedule';

export interface ProductDeposit {
    amountMinor: number;
    source: 'default' | 'minimum';
}

interface ProductDepositSettings {
    allowDeposit?: boolean;
    defaultDepositAmountMinor?: number;
    minDepositAmountMinor?: number;
    maxDepositAmountMinor?: number;
}

export function resolveProductDepositMinor(
    product: ProductDepositSettings | null | undefined,
): ProductDeposit | undefined {
    if (product?.allowDeposit !== true) {
        return undefined;
    }
    const { minDepositAmountMinor: min, maxDepositAmountMinor: max } = product;
    const usableDefault = usableDefaultDepositMinor(product.defaultDepositAmountMinor, min, max);
    if (usableDefault !== undefined) {
        return { amountMinor: usableDefault, source: 'default' };
    }
    if (isPositiveInteger(min) && (max === undefined || min <= max)) {
        return { amountMinor: min, source: 'minimum' };
    }
    return undefined;
}

export function seedDepositMinor(
    selection: PaymentSelection | undefined,
    productDeposit: Pick<ProductDeposit, 'amountMinor'> | undefined,
): { amountMinor: number | undefined; origin: DepositOrigin } {
    if (isValidPaymentSelection(selection)) {
        if (selection.params.depositAmount !== undefined) {
            return { amountMinor: selection.params.depositAmount, origin: 'confirmed' };
        }
        if (selection.params.hasDeposit === false) {
            return { amountMinor: 0, origin: 'confirmed' };
        }
    }
    return productDeposit !== undefined
        ? { amountMinor: productDeposit.amountMinor, origin: 'product' }
        : { amountMinor: undefined, origin: 'blank' };
}

export function shouldDiscardProductDeposit(options: {
    isReadOnly: boolean;
    isDepositBearing: boolean;
    isBoundsSettled: boolean;
    origin: DepositOrigin;
    depositMinor: number | undefined;
    effectiveMaxExclusive: number | undefined;
}): boolean {
    const { depositMinor, effectiveMaxExclusive } = options;
    return (
        !options.isReadOnly &&
        options.isDepositBearing &&
        options.isBoundsSettled &&
        options.origin === 'product' &&
        depositMinor !== undefined &&
        effectiveMaxExclusive !== undefined &&
        depositMinor >= effectiveMaxExclusive
    );
}

export function scheduleDepositToWrite(options: {
    isReadOnly: boolean;
    origin: DepositOrigin;
    params: SchedulePreviewParams | null;
    hasPreview: boolean;
    hasError: boolean;
    isFetching: boolean;
    isPlaceholderData: boolean;
    isPricingUpdating: boolean;
    appliedDeposit: AppliedDeposit | undefined;
    minDepositMinor: number | undefined;
    effectiveMaxExclusive: number | undefined;
}): number | undefined {
    const { params, appliedDeposit, effectiveMaxExclusive } = options;
    const isSettledBlankPreview =
        params !== null &&
        params.hasDeposit === true &&
        params.depositAmount === undefined &&
        options.hasPreview &&
        !options.hasError &&
        !options.isFetching &&
        !options.isPlaceholderData &&
        !options.isPricingUpdating;
    if (options.isReadOnly || options.origin !== 'blank' || !isSettledBlankPreview) {
        return undefined;
    }
    if (appliedDeposit?.kind !== 'amount' || appliedDeposit.source !== 'schedule') {
        return undefined;
    }
    const figure = appliedDeposit.amountMinor;
    const floor = Math.max(options.minDepositMinor ?? 0, 1);
    const fitsSchema = effectiveMaxExclusive !== undefined && figure >= floor && figure < effectiveMaxExclusive;
    return fitsSchema ? figure : undefined;
}
